import React, { useEffect, useState, useCallback } from "react";
import { api, extractErr, API_ROOT } from "@/lib/api";
import { Skeleton } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Calculator, Download, FileSpreadsheet, RefreshCw, Lock } from "lucide-react";
import { toast } from "sonner";

function fmtMoney(v, currency = "EUR") {
  const n = Number(v || 0);
  const sym = currency === "EUR" ? "€" : (currency + " ");
  return `${sym}${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtUnit(v, currency = "EUR") {
  const n = Number(v || 0);
  const sym = currency === "EUR" ? "€" : (currency + " ");
  return `${sym}${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`;
}

export function ProductionCostPanel({ orderId, status }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [downloading, setDownloading] = useState("");

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const { data } = await api.get(`/production/orders/${orderId}/cost-estimate`);
      setData(data);
    } catch (e) {
      toast.error(extractErr(e, "Failed to compute cost estimate"));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [orderId]);

  useEffect(() => { if (orderId) load(); }, [orderId, load]);

  const download = async (kind) => {
    setDownloading(kind);
    try {
      const token = localStorage.getItem("operon_token");
      const url = `${API_ROOT}/production/orders/${orderId}/cost-estimate/${kind}`;
      const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) throw new Error(`Server returned ${resp.status}`);
      const blob = await resp.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `cost-${orderId}.${kind === "pdf" ? "pdf" : "xlsx"}`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch (e) {
      toast.error(`Failed to download ${kind.toUpperCase()}: ${e.message}`);
    } finally {
      setDownloading("");
    }
  };

  if (loading && !data) {
    return <div className="rounded-2xl border border-border bg-card p-4"><Skeleton className="h-32" /></div>;
  }
  if (!data) return null;

  const cur = data.currency || "EUR";
  const isLocked = !data.is_estimate;

  return (
    <div className="rounded-2xl border border-border bg-card p-4" data-testid="production-cost-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="font-heading font-semibold flex items-center gap-2">
          <Calculator className="w-4 h-4 text-primary" /> Production cost estimate
          {isLocked && <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-[11px] px-2 py-0.5" data-testid="production-cost-locked"><Lock className="w-3 h-3" /> Locked at completion</span>}
          {!isLocked && <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 text-primary text-[11px] px-2 py-0.5" data-testid="production-cost-live">Live estimate</span>}
        </div>
        <div className="flex items-center gap-1.5">
          <Button size="sm" variant="ghost" onClick={load} disabled={refreshing} data-testid="production-cost-refresh">
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
          </Button>
          <Button size="sm" variant="outline" onClick={() => download("pdf")} disabled={!!downloading} data-testid="production-cost-pdf">
            <Download className="w-3.5 h-3.5 mr-1" /> PDF
          </Button>
          <Button size="sm" variant="outline" onClick={() => download("excel")} disabled={!!downloading} data-testid="production-cost-excel">
            <FileSpreadsheet className="w-3.5 h-3.5 mr-1" /> Excel
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        <Tile label="Quantity" value={`${data.quantity_produced}`} testid="production-cost-qty" />
        <Tile label="Raw materials" value={fmtMoney(data.raw_material_cost, cur)} testid="production-cost-raw" />
        <Tile label="Packaging" value={fmtMoney(data.packaging_cost, cur)} testid="production-cost-packaging" />
        <Tile label="Total" value={fmtMoney(data.total_material_cost, cur)} highlight testid="production-cost-total" />
      </div>

      <div className="flex items-center justify-between rounded-xl border border-primary/20 bg-primary/5 px-3 py-2 mb-3">
        <div className="text-sm text-primary font-medium">Cost per unit</div>
        <div className="text-xl font-semibold tabular-nums text-primary" data-testid="production-cost-per-unit">
          {fmtUnit(data.cost_per_unit, cur)}
        </div>
      </div>

      {(data.breakdown || []).length > 0 ? (
        <div className="rounded-xl border border-border overflow-hidden">
          <div className="grid grid-cols-12 px-3 py-2 text-[11px] uppercase tracking-wider text-muted-foreground border-b border-border bg-muted/40">
            <div className="col-span-2">Role</div>
            <div className="col-span-3">SKU</div>
            <div className="col-span-3">Item</div>
            <div className="col-span-1 text-right">Qty</div>
            <div className="col-span-2 text-right">Unit cost</div>
            <div className="col-span-1 text-right">Line</div>
          </div>
          <div className="max-h-72 overflow-y-auto divide-y divide-border">
            {data.breakdown.map((b, i) => (
              <div key={i} className="grid grid-cols-12 px-3 py-2 text-sm items-center" data-testid={`production-cost-line-${i}`}>
                <div className="col-span-2 capitalize text-muted-foreground">{(b.role || "").replace(/_/g, " ")}</div>
                <div className="col-span-3 font-mono text-xs">{b.item_sku || "—"}</div>
                <div className="col-span-3 truncate">{b.item_name}</div>
                <div className="col-span-1 text-right tabular-nums">{Number(b.qty).toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
                <div className="col-span-2 text-right tabular-nums">{fmtUnit(b.unit_cost, b.currency || cur)}</div>
                <div className="col-span-1 text-right tabular-nums font-medium">{fmtMoney(b.line_cost, b.currency || cur)}</div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="rounded-xl border border-dashed border-border px-3 py-4 text-sm text-muted-foreground" data-testid="production-cost-empty">
          No BOM components found for this order. Add a Size BOM (packaging) or product BOM to compute material cost.
        </div>
      )}

      <div className="text-[11px] text-muted-foreground mt-3">
        Material &amp; packaging cost only. Labour and overhead are excluded.
      </div>
    </div>
  );
}

function Tile({ label, value, highlight, testid }) {
  return (
    <div className={`rounded-xl border ${highlight ? "border-primary/30 bg-primary/5" : "border-border bg-background"} p-3`} data-testid={testid}>
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`text-lg font-semibold tabular-nums mt-1 ${highlight ? "text-primary" : ""}`}>{value}</div>
    </div>
  );
}
